import {
  getAppUsageMetricsData,
  getCustomerList,
  getUsageMetricsData,
  getApiUsageMetricsData,
} from '@src/api/usageDashboard';
import { productInitFields } from '@utils/constants';
import { trackQueryError } from '@utils/sensor/apptracker';
import { useAtomValue, useSetAtom } from 'jotai';
import { cloneDeep, get, isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  appAccountsDetailsAtom,
  granularityAtom,
  intervalAtom,
  usageMetricList,
  videoAccountsDetailsAtom,
  apiAccountsDetailsAtom,
} from '../atoms';
import { transformDataForChart } from './utils';

const defaultMetricValue = { total: null, data: [] };
const defaultResponseValue = { totals: {}, data: {} };

export function useGetCustomerList({ enabled = false }) {
  const setVideoAccountsDetails = useSetAtom(videoAccountsDetailsAtom);
  const setApiAccountsDetails = useSetAtom(apiAccountsDetailsAtom);
  const setAppAccountsDetails = useSetAtom(appAccountsDetailsAtom);
  const { isFetching, isError, data, refetch } = useQuery(
    ['account-list-for-usage-dashboard', {}],
    () => {
      return getCustomerList().then((res) => {
        return res.data;
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: null,
      onError: (err) => {
        trackQueryError({ type: 'account-list-for-usage-dashboard', err });
      },
    },
  );
  if (!isFetching) {
    setVideoAccountsDetails(cloneDeep(data.videoCustomerList));
    setAppAccountsDetails(cloneDeep(data.appCustomerList));
    setApiAccountsDetails(cloneDeep(data.apiCustomerList));
  }
  return { isFetching, isError, data, refetch };
}

export function useMakeReqBody(product, accountsDetails) {
  const changedInterval: any = useAtomValue(intervalAtom);
  const granularity = useAtomValue(granularityAtom);
  const interval = `${changedInterval[0].format()}/${changedInterval[1].format()}`;
  const metrics = Object.keys(usageMetricList).filter(
    (metric) => usageMetricList[metric].product === product,
  );

  const selectedAccounts = accountsDetails
    .filter((cust) => cust.isSelected === true)
    .map((c) => c.customerId);

  const reqBody: any = {
    queries: [
      {
        type: 'timeseries',
        dataset: productInitFields[product].DATASET,
        filter: [],
        metrics,
        limit: 200,
        interval,
        granularity,
        isLiveMode: false,
        options: {
          withTotals: true,
        },
        accountIds: selectedAccounts,
      },
    ],
  };
  return { reqBody, metrics, interval };
}

export function useFetchVideoUsageMetric({ enabled = false, product, metric = null }) {
  const videoAccountsDetails: any = useAtomValue(videoAccountsDetailsAtom);
  const { reqBody, metrics } = useMakeReqBody(product, videoAccountsDetails);
  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    ['ei-usage-metric-series', reqBody],
    () => {
      return getUsageMetricsData(reqBody).then((res) => {
        let { data, totals } = get(res, ['data', '0'], []);
        ({ data, totals } = transformDataForChart({
          metrics,
          data,
          totals,
        }));
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: defaultResponseValue,
      onError: (err) => {
        trackQueryError({ type: 'ei-usage-metric-series', err });
      },
    },
  );
  const data = useMemo(() => {
    if (isEmpty(response)) {
      return metric ? defaultMetricValue : defaultResponseValue;
    }

    if (metric) {
      const { data, totals } = response;
      if (!data[metric] || !totals[metric]) return defaultMetricValue;
      return { total: totals[metric], data: data[metric] };
    }

    return response;
  }, [metric, response]);

  return { isFetching, isError, data, refetch };
}

export function useFetchAPIUsageMetric({ enabled = false, product, metric = null }) {
  const apiAccountsDetails: any = useAtomValue(apiAccountsDetailsAtom);
  const { reqBody, metrics } = useMakeReqBody(product, apiAccountsDetails);
  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    ['api-usage-metric-series', reqBody],
    () => {
      return getApiUsageMetricsData(reqBody).then((res) => {
        let { data, totals } = get(res, ['data', '0'], []);
        ({ data, totals } = transformDataForChart({
          metrics,
          data,
          totals,
        }));
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: defaultResponseValue,
      onError: (err) => {
        trackQueryError({ type: 'api-usage-metric-series', err });
      },
    },
  );
  const data = useMemo(() => {
    if (isEmpty(response)) {
      return metric ? defaultMetricValue : defaultResponseValue;
    }

    if (metric) {
      const { data, totals } = response;
      if (!data[metric] || !totals[metric]) return defaultMetricValue;
      return { total: totals[metric], data: data[metric] };
    }

    return response;
  }, [metric, response]);

  return { isFetching, isError, data, refetch };
}

export function useFetchAppUsageMetric({ enabled = false, product, metric = null }) {
  const appAccountsDetails: any = useAtomValue(appAccountsDetailsAtom);
  const { reqBody, metrics } = useMakeReqBody(product, appAccountsDetails);
  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    ['app-usage-metric-series', reqBody],
    () => {
      return getAppUsageMetricsData(reqBody).then((res) => {
        let { data, totals } = get(res, ['data', '0'], []);
        ({ data, totals } = transformDataForChart({
          metrics,
          data,
          totals,
        }));
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: defaultResponseValue,
      onError: (err) => {
        trackQueryError({ type: 'app-usage-metric-series', err });
      },
    },
  );
  const data = useMemo(() => {
    if (isEmpty(response)) {
      return metric ? defaultMetricValue : defaultResponseValue;
    }

    if (metric) {
      const { data, totals } = response;
      if (!data[metric] || !totals[metric]) return defaultMetricValue;
      return { total: totals[metric], data: data[metric] };
    }

    return response;
  }, [metric, response]);

  return { isFetching, isError, data, refetch };
}
